import { Injectable, signal } from '@angular/core';
import type { Gender, UserDto, PreferencesDto } from '@lumio/shared';

const STORAGE_KEY = 'lumio_auth_form_state';

interface PersistedState {
  email?: string;
  cpf?: string;
  fullNumber?: string;
  fullName?: string;
  gender?: string;
  dateOfBirth?: string;
  countryOfBirth?: string;
  countryOfResidence?: string;
  legalAgeConsent?: boolean;
  termsAcceptance?: boolean;
  sportsbooks?: string[];
  favoriteTeamId?: string | null;
  experienceLevel?: string | null;
  riskLevel?: string | null;
  telegramNotifications?: boolean;
  inAppNotifications?: boolean;
  emailNotifications?: boolean;
  communicationsAccepted?: boolean;
}

function load(): PersistedState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function save(state: PersistedState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage unavailable (private mode quota exceeded etc.)
  }
}

/** Persists auth form values when navigating back/forward in the auth flow — survives page refresh */
@Injectable({ providedIn: 'root' })
export class AuthFormStateService {
  private _saved: PersistedState = load();

  readonly email = signal(this._saved.email ?? '');
  readonly password = signal(''); // never persisted
  readonly cpf = signal(this._saved.cpf ?? '');
  readonly fullNumber = signal(this._saved.fullNumber ?? '');
  readonly otp = signal(''); // never persisted
  /** Perfil — PUT /users/me/profile */
  readonly fullName = signal(this._saved.fullName ?? '');
  readonly gender = signal<Gender>((this._saved.gender as Gender) ?? 'OTHER');
  readonly dateOfBirth = signal(this._saved.dateOfBirth ?? '');
  readonly countryOfBirth = signal(this._saved.countryOfBirth ?? '');
  readonly countryOfResidence = signal(this._saved.countryOfResidence ?? '');
  readonly legalAgeConsent = signal(this._saved.legalAgeConsent ?? false);
  readonly termsAcceptance = signal(this._saved.termsAcceptance ?? false);
  /** Preferências — POST /users/preferences */
  readonly sportsbooks = signal<string[]>(this._saved.sportsbooks ?? []);
  readonly favoriteTeamId = signal<string | null>(this._saved.favoriteTeamId ?? null);
  readonly experienceLevel = signal<'beginner' | 'intermediate' | 'expert' | null>(
    (this._saved.experienceLevel as 'beginner' | 'intermediate' | 'expert') ?? null
  );
  readonly riskLevel = signal<'low' | 'medium' | 'high' | null>(
    (this._saved.riskLevel as 'low' | 'medium' | 'high') ?? null
  );
  readonly telegramNotifications = signal<boolean>(this._saved.telegramNotifications ?? false);
  readonly inAppNotifications = signal<boolean>(this._saved.inAppNotifications ?? false);
  readonly emailNotifications = signal<boolean>(this._saved.emailNotifications ?? false);
  readonly communicationsAccepted = signal<boolean>(this._saved.communicationsAccepted ?? true);

  private persist(): void {
    save({
      email: this.email(),
      cpf: this.cpf(),
      fullNumber: this.fullNumber(),
      fullName: this.fullName(),
      gender: this.gender(),
      dateOfBirth: this.dateOfBirth(),
      countryOfBirth: this.countryOfBirth(),
      countryOfResidence: this.countryOfResidence(),
      legalAgeConsent: this.legalAgeConsent(),
      termsAcceptance: this.termsAcceptance(),
      sportsbooks: this.sportsbooks(),
      favoriteTeamId: this.favoriteTeamId(),
      experienceLevel: this.experienceLevel(),
      riskLevel: this.riskLevel(),
      telegramNotifications: this.telegramNotifications(),
      inAppNotifications: this.inAppNotifications(),
      emailNotifications: this.emailNotifications(),
      communicationsAccepted: this.communicationsAccepted(),
    });
  }

  setEmail(value: string): void {
    this.email.set(value);
    this.persist();
  }

  setPassword(value: string): void {
    this.password.set(value); // not persisted
  }

  setCpf(value: string): void {
    this.cpf.set(value);
    this.persist();
  }

  setFullNumber(value: string): void {
    this.fullNumber.set(value);
    this.persist();
  }

  setFullName(value: string): void {
    this.fullName.set(value);
    this.persist();
  }

  setGender(value: Gender): void {
    this.gender.set(value);
    this.persist();
  }

  setDateOfBirth(value: string): void {
    this.dateOfBirth.set(value);
    this.persist();
  }

  setCountryOfBirth(value: string): void {
    this.countryOfBirth.set(value);
    this.persist();
  }

  setCountryOfResidence(value: string): void {
    this.countryOfResidence.set(value);
    this.persist();
  }

  setLegalAgeConsent(value: boolean): void {
    this.legalAgeConsent.set(value);
    this.persist();
  }

  setTermsAcceptance(value: boolean): void {
    this.termsAcceptance.set(value);
    this.persist();
  }

  setCommunicationsAccepted(value: boolean): void {
    this.communicationsAccepted.set(value);
    this.persist();
  }

  setOtp(value: string): void {
    this.otp.set(value); // not persisted
  }

  setSportsbooks(value: string[]): void {
    this.sportsbooks.set(value);
    this.persist();
  }

  setFavoriteTeam(id: string | null): void {
    this.favoriteTeamId.set(id);
    this.persist();
  }

  setExperienceLevel(value: 'beginner' | 'intermediate' | 'expert' | null): void {
    this.experienceLevel.set(value);
    this.persist();
  }

  setRiskLevel(value: 'low' | 'medium' | 'high' | null): void {
    this.riskLevel.set(value);
    this.persist();
  }

  setTelegramNotifications(value: boolean): void {
    this.telegramNotifications.set(value);
    this.persist();
  }

  setInAppNotifications(value: boolean): void {
    this.inAppNotifications.set(value);
    this.persist();
  }

  setEmailNotifications(value: boolean): void {
    this.emailNotifications.set(value);
    this.persist();
  }

  /** Clear all values and sessionStorage (called when auth completes or user logs out) */
  clear(): void {
    sessionStorage.removeItem(STORAGE_KEY);
    this.email.set('');
    this.password.set('');
    this.cpf.set('');
    this.fullNumber.set('');
    this.otp.set('');
    this.fullName.set('');
    this.gender.set('OTHER');
    this.dateOfBirth.set('');
    this.countryOfBirth.set('');
    this.countryOfResidence.set('');
    this.legalAgeConsent.set(false);
    this.termsAcceptance.set(false);
    this.sportsbooks.set([]);
    this.favoriteTeamId.set(null);
    this.experienceLevel.set(null);
    this.riskLevel.set(null);
    this.telegramNotifications.set(false);
    this.inAppNotifications.set(false);
    this.emailNotifications.set(false);
    this.communicationsAccepted.set(true);
  }

  /**
   * Preenche o state com dados do /users/me ao redirecionar para o fluxo de registro.
   * Não preenche password nem cpf por segurança.
   * Só sobrescreve campos que ainda estão vazios no state (prioriza o que o usuário já digitou).
   */
  seedFromUser(user: UserDto): void {
    if (user.email && !this.email()) this.setEmail(user.email);
    const p = user.profile;
    if (p) {
      if (p.phone && !this.fullNumber()) this.setFullNumber(p.phone);
      if (p.fullName && !this.fullName()) this.setFullName(p.fullName);
      if (p.gender && this.gender() === 'OTHER') this.setGender(p.gender as Gender);
      if (p.dateOfBirth && !this.dateOfBirth()) this.setDateOfBirth(p.dateOfBirth);
      if (p.countryOfBirth && !this.countryOfBirth()) this.setCountryOfBirth(p.countryOfBirth);
      if (p.countryOfResidence && !this.countryOfResidence()) this.setCountryOfResidence(p.countryOfResidence);
    }
  }

  /**
   * Preenche o state com dados do /users/preferences ao redirecionar para o fluxo de registro.
   * Só sobrescreve campos que ainda estão vazios/null no state.
   */
  seedFromPreferences(preferences: PreferencesDto): void {
    if (!this.sportsbooks().length && preferences.preferredSportsbooks?.length) {
      this.setSportsbooks(preferences.preferredSportsbooks);
    }
    if (!this.favoriteTeamId() && preferences.favoriteClubId) {
      this.setFavoriteTeam(String(preferences.favoriteClubId));
    }
    if (!this.experienceLevel() && preferences.bettingExperience) {
      const exp = preferences.bettingExperience.toLowerCase();
      const mapped = exp === 'advanced' ? 'expert' : exp === 'intermediate' ? 'intermediate' : 'beginner';
      this.setExperienceLevel(mapped as 'beginner' | 'intermediate' | 'expert');
    }
    if (!this.riskLevel() && preferences.riskAppetite) {
      const risk = preferences.riskAppetite.toLowerCase();
      const mapped = risk === 'high' ? 'high' : risk === 'medium' ? 'medium' : 'low';
      this.setRiskLevel(mapped as 'low' | 'medium' | 'high');
    }
    if (preferences.notificationChannels?.length) {
      this.setEmailNotifications(preferences.notificationChannels.includes('EMAIL'));
      this.setInAppNotifications(preferences.notificationChannels.includes('IN_APP'));
      this.setTelegramNotifications(preferences.notificationChannels.includes('WHATSAPP'));
    }
  }
}
